import { useOutletContext } from 'react-router-dom'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import { isMyExpensesSection } from '../lib/common'
import { createGroupPolicyTarget, isPendingGroup, isRejectedGroup } from '../lib/groups/group'
import { Group, useGroup } from './useGroup'

interface UseGroupDetailReturn {
  group?: Group
  loading: boolean
}

interface UseGroupDetail {
  id: number
  includeAttachments?: boolean
}

export const useGroupDetail = ({
  id,
  includeAttachments,
}: UseGroupDetail): UseGroupDetailReturn => {
  const { section, inExpiredFreeTrial } = useOutletContext<RouteContextParams>()
  const { employee } = useCurrentSession()

  const { group, loading } = useGroup({
    id,
    includeAttachments,
    includeDrafts: section === 'personal',
    employeeId: isMyExpensesSection(section) ? employee?.id : undefined,
    skip: inExpiredFreeTrial,
  })

  useAsyncData(() => {
    if (!group) return Promise.resolve()
    const promises = []
    const target = createGroupPolicyTarget(group)

    if (isPendingGroup(group)) {
      promises.push(policyAuthorizations.authorizePolicies('expensable_groups.edit', [target]))
    }

    if (isRejectedGroup(group)) {
      promises.push(policyAuthorizations.authorizePolicies('expensable_groups.delete', [target]))
    }

    return Promise.all(promises)
  }, [group?.status])

  return { group, loading }
}
